<template>
  <v-dialog :value="value" max-width="560" @input="$emit('input', $event)">
    <v-card class="about-dialog">
      <v-card-title class="py-3">
        <v-icon left color="primary">mdi-information-outline</v-icon>
        <span>About Virtual Profiler</span>
        <v-spacer />
        <v-btn icon small aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

        <v-simple-table dense class="components-table">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">Component</th>
                <th class="text-left">Version</th>
                <th class="text-left">Build</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in componentRows" :key="row.key" :data-component="row.key">
                <td>{{ row.label }}</td>
                <td class="version-cell">{{ row.version }}</td>
                <td>
                  <code class="build-sha" :class="{ 'build-sha--dev': row.buildSha === 'dev' }">
                    {{ row.buildSha }}
                  </code>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div v-if="loadError" class="mt-3 caption error--text load-error">
          {{ loadError }}
        </div>

        <div class="mt-4 subtitle-2">Engines</div>
        <div v-if="engineChips.length" class="engine-chips mt-1">
          <v-chip
            v-for="engine in engineChips"
            :key="engine.name"
            small
            outlined
            class="mr-1 mb-1 engine-chip"
            :data-engine="engine.name"
          >
            {{ engine.name }} {{ engine.version }}
          </v-chip>
        </div>
        <div v-else class="caption grey--text mt-1 engines-empty">
          {{ loading ? 'Loading…' : 'Engine versions unavailable (ai-service not reachable).' }}
        </div>

        <template v-if="schemaChips.length">
          <div class="mt-3 subtitle-2">Schemas</div>
          <div class="mt-1">
            <v-chip
              v-for="schema in schemaChips"
              :key="schema.name"
              x-small
              outlined
              class="mr-1 mb-1 schema-chip"
            >
              {{ schema.name }} {{ schema.version }}
            </v-chip>
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VersionService from '@/services/VersionService';
import utils from '@/utils/utils';

const UNKNOWN = 'unknown';

/** Stable display order of the seven engines (matches ai-service app/versions.py). */
export const ENGINE_ORDER = Object.freeze([
  'gestures',
  'coherence',
  'cultural_fit',
  'client_questions',
  'pre_competency',
  'toolkit',
  'cv',
]);

function toChips(record, order = []) {
  if (!record || typeof record !== 'object') return [];
  const names = Object.keys(record);
  const ordered = [
    ...order.filter((name) => names.includes(name)),
    ...names.filter((name) => !order.includes(name)).sort(),
  ];
  return ordered.map((name) => ({ name, version: String(record[name]) }));
}

export default {
  name: 'AboutDialog',

  props: {
    /** v-model: dialog visibility. */
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      loadError: null,
      versionInfo: null,
    };
  },

  computed: {
    frontend() {
      return { version: utils.app.version, buildSha: utils.app.buildSha };
    },
    api() {
      const info = this.versionInfo;
      return {
        version: info?.tool_version || UNKNOWN,
        buildSha: info?.build_sha || UNKNOWN,
      };
    },
    aiService() {
      const info = this.versionInfo?.ai_service;
      return {
        version: info?.tool_version || UNKNOWN,
        buildSha: info?.build_sha || UNKNOWN,
      };
    },
    componentRows() {
      return [
        { key: 'frontend', label: 'Frontend', ...this.frontend },
        { key: 'api', label: 'API (Node)', ...this.api },
        { key: 'ai-service', label: 'AI service', ...this.aiService },
      ];
    },
    engineChips() {
      return toChips(this.versionInfo?.ai_service?.engines, ENGINE_ORDER);
    },
    schemaChips() {
      return toChips(this.versionInfo?.ai_service?.schema_versions);
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.load();
      },
    },
  },

  methods: {
    async load() {
      this.loading = true;
      this.loadError = null;
      try {
        const response = await VersionService.get();
        this.versionInfo = response?.data || null;
      } catch (error) {
        this.versionInfo = null;
        this.loadError = 'Could not load the backend versions.';
        console.error('[AboutDialog] Version lookup failed:', error);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.version-cell {
  font-variant-numeric: tabular-nums;
}

.build-sha {
  font-size: 12px;
}

.build-sha--dev {
  color: #fb8c00;
}

.engine-chips {
  display: flex;
  flex-wrap: wrap;
}
</style>
